import { useState } from "react";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { PERSONAS } from "../data/org";
import { profileOf } from "../data/profiles";
import { generateAgentSystemPromptAI } from "../lib/claude";
import { initials, monogramColor } from "../lib/monogram";

export function AgentPromptEditor({ personaId, onClose }:
  { personaId: string; onClose: () => void }) {
  const persona = PERSONAS.find(p => p.id === personaId);
  const employee = useRH(s => s.employees[personaId]);
  const setSystemPrompt = useRH(s => s.setSystemPrompt);
  const settings = useSettings();

  const prof = persona ? profileOf(persona.id, persona.tier) : undefined;
  const defaultPrompt = persona && prof
    ? `Você é ${persona.name}, da AGÊNCIA VPC.\n` +
      `- Habilidades: ${prof.skills.join(", ")}\n` +
      `- Expectativa: ${prof.expectations}\n` +
      `- Voz característica: ${prof.voice}`
    : "";

  const [draft, setDraft] = useState(employee?.systemPromptOverride ?? "");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);

  if (!persona || !prof) return null;

  const generate = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setBusy(true);
    try {
      const text = await generateAgentSystemPromptAI({
        apiKey: settings.anthropicKey,
        model: settings.model,
        personaName: persona.name,
        personaBio: persona.bio,
        skills: prof.skills,
        voice: prof.voice,
        expectations: prof.expectations,
        deliverables: prof.deliverables,
        hint
      });
      setDraft(text);
    } catch (e) { alert("Falha: " + (e as Error).message); }
    finally { setBusy(false); }
  };

  const save = () => {
    setSystemPrompt(persona.id, draft.trim());
    alert("System prompt salvo.");
    onClose();
  };

  const reset = () => {
    if (!confirm("Voltar pro prompt padrão (gerado a partir do profile)?")) return;
    setSystemPrompt(persona.id, "");
    setDraft("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-3xl max-h-[92vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-ink text-bone px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 flex items-center justify-center font-display font-black"
                 style={{ background: monogramColor(persona.id), color: "#F2EEE6" }}>
              {initials(persona.name)}
            </div>
            <div className="min-w-0">
              <div className="font-mono text-xs text-gold">PROMPT-01</div>
              <h2 className="font-display font-black uppercase text-2xl leading-none truncate">
                Prompt de {persona.name}
              </h2>
              <div className="font-crimson italic text-bone/70 text-sm truncate">{persona.bio}</div>
            </div>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone shrink-0">fechar ×</button>
        </header>

        <div className="p-5 space-y-4">
          {/* Default preview */}
          <section className="border-2 border-ink/30 p-3 bg-bone">
            <div className="tag text-graydk mb-2">▸ prompt padrão (gerado do profile)</div>
            <pre className="font-mono text-[11px] whitespace-pre-wrap leading-relaxed text-paper">
{defaultPrompt}
            </pre>
            <div className="tag text-graydk mt-2 italic">
              skills: {prof.skills.join(" · ")} · capacidade: {prof.capacity}
            </div>
          </section>

          {/* IA generator */}
          <section className="border-2 border-ink p-3 bg-gold/10">
            <div className="font-display font-bold uppercase text-sm mb-2">▸ gerar prompt aprimorado com IA</div>
            <div className="tag text-graydk mb-1">direcionamento opcional</div>
            <input
              value={hint} onChange={(e) => setHint(e.target.value)}
              placeholder="ex: foco em performance, evite jargão, sempre cite números"
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px] mb-2"
            />
            <button onClick={generate} disabled={busy}
              className="btn bg-ink text-bone border-ink disabled:opacity-50">
              {busy ? "gerando…" : "✦ gerar system prompt detalhado"}
            </button>
          </section>

          {/* Editor */}
          <section className="border-2 border-ink p-3 bg-bone">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display font-bold uppercase text-sm">
                ▸ prompt em uso {employee?.systemPromptOverride ? "(customizado)" : "(usando padrão)"}
              </div>
              {employee?.systemPromptOverride && (
                <button onClick={reset} className="tag text-red underline">↺ voltar ao padrão</button>
              )}
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={14}
              placeholder="(vazio = usa o prompt padrão acima)"
              className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[12px] resize-y leading-relaxed"
            />
            <div className="tag text-graydk mt-1">{draft.length} chars</div>
          </section>

          <div className="flex justify-end gap-2 pt-2 border-t border-ink/30">
            <button onClick={onClose} className="btn btn-fire">cancelar</button>
            <button onClick={save} className="btn btn-hire">salvar prompt</button>
          </div>

          <p className="font-crimson italic text-paper text-[11px] text-center">
            Quando este agente for designado em uma demanda e você clicar "▶ auto-executar",
            ele vai usar este prompt em vez do padrão.
          </p>
        </div>
      </div>
    </div>
  );
}
